/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace exponential
*/
var exponential = {};

/**
* @name cdf
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/cdf}
*/
import cdf from './../../../../base/dists/exponential/cdf';
setReadOnly( exponential, 'cdf', cdf );

/**
* @name Exponential
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/ctor}
*/
import Exponential from './../../../../base/dists/exponential/ctor';
setReadOnly( exponential, 'Exponential', Exponential );

/**
* @name entropy
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/entropy}
*/
import entropy from './../../../../base/dists/exponential/entropy';
setReadOnly( exponential, 'entropy', entropy );

/**
* @name kurtosis
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/kurtosis}
*/
import kurtosis from './../../../../base/dists/exponential/kurtosis';
setReadOnly( exponential, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/logcdf}
*/
import logcdf from './../../../../base/dists/exponential/logcdf';
setReadOnly( exponential, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/logpdf}
*/
import logpdf from './../../../../base/dists/exponential/logpdf';
setReadOnly( exponential, 'logpdf', logpdf );

/**
* @name mean
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/mean}
*/
import mean from './../../../../base/dists/exponential/mean';
setReadOnly( exponential, 'mean', mean );

/**
* @name median
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/median}
*/
import median from './../../../../base/dists/exponential/median';
setReadOnly( exponential, 'median', median );

/**
* @name mgf
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/mgf}
*/
import mgf from './../../../../base/dists/exponential/mgf';
setReadOnly( exponential, 'mgf', mgf );

/**
* @name mode
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/mode}
*/
import mode from './../../../../base/dists/exponential/mode';
setReadOnly( exponential, 'mode', mode );

/**
* @name pdf
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/pdf}
*/
import pdf from './../../../../base/dists/exponential/pdf';
setReadOnly( exponential, 'pdf', pdf );

/**
* @name quantile
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/quantile}
*/
import quantile from './../../../../base/dists/exponential/quantile';
setReadOnly( exponential, 'quantile', quantile );

/**
* @name skewness
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/skewness}
*/
import skewness from './../../../../base/dists/exponential/skewness';
setReadOnly( exponential, 'skewness', skewness );

/**
* @name stdev
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/stdev}
*/
import stdev from './../../../../base/dists/exponential/stdev';
setReadOnly( exponential, 'stdev', stdev );

/**
* @name variance
* @memberof exponential
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/exponential/variance}
*/
import variance from './../../../../base/dists/exponential/variance';
setReadOnly( exponential, 'variance', variance );


// EXPORTS //

export default exponential;
